import { useQueryClient } from "@tanstack/react-query";
import FetchClients from "../client";
import { PROJECT_KEY } from "./project";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";

const notes = { mutations: { usePost, usePatch, useDelete } } as const;
export default notes;

// endpoint requires {feature: "Projects", access: "edit"}
async function addNote(
  projectId: number,
  note: string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.projects.POST(
    "/{project_id}/note",
    {
      params: {
        path: { project_id: projectId },
      },
      body: { body: note },
      headers: checkFeatureAccessHeader,
    }
  );

  if (data) return data;
  throw error;
}
function usePost({ siteId, projectId }: { siteId: number; projectId: number }) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    (note: string, checkFeatureAccessHeader) =>
      addNote(projectId, note, checkFeatureAccessHeader),
    {
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: [PROJECT_KEY, siteId],
        }),
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
async function patchNote(
  projectId: number,
  note: string,
  noteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.projects.PATCH(
    "/{project_id}/note/{note_id}",
    {
      params: {
        path: { project_id: projectId, note_id: noteId },
      },
      body: { body: note },
      headers: checkFeatureAccessHeader,
    }
  );

  if (data) return data;
  throw error;
}

function usePatch({
  siteId,
  projectId,
}: {
  siteId: number;
  projectId: number;
}) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    (
      { noteId, note }: { noteId: number; note: string },
      checkFeatureAccessHeader
    ) => patchNote(projectId, note, noteId, checkFeatureAccessHeader),
    {
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: [PROJECT_KEY, siteId],
        }),
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
async function deleteNote(
  projectId: number,
  noteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { error } = await FetchClients.projects.DELETE(
    "/{project_id}/note/{note_id}",
    {
      params: {
        path: { project_id: projectId, note_id: noteId },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (error) throw error;
}

function useDelete({
  siteId,
  projectId,
}: {
  siteId: number;
  projectId: number;
}) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    (noteId: number, checkFeatureAccessHeader) =>
      deleteNote(projectId, noteId, checkFeatureAccessHeader),
    {
      onSettled: () =>
        queryClient.invalidateQueries({
          queryKey: [PROJECT_KEY, siteId],
        }),
    }
  );
}
